import React, { useEffect, useState } from 'react';
import { Alert, TextField } from '@mui/material';

import ConfirmationDialog, { ConfirmationDialogProps, ConfirmFacts } from './ConfirmationDialog';

export interface ReasonDialogProps {
  open: boolean;
  onClose: () => void;
  /** e.g. "Reverse receipt RCP-2026-000014?" */
  title: string;
  /** What the action touches — amount, payer, date… one line each. */
  facts?: Array<{ label: React.ReactNode; value: React.ReactNode }>;
  /** What confirming does, in plain words. */
  consequence: string;
  confirmText?: string;
  confirmColor?: ConfirmationDialogProps['confirmColor'];
  reasonPlaceholder?: string;
  /** Runs the action; throw to keep the dialog open with the message shown. */
  onConfirm: (reason: string) => Promise<void>;
}

/**
 * Confirm an action that must record why — reversing money, rejecting a
 * journal, reopening a month. Says what is affected and what confirming
 * does; the reason is required and saved with the record for audit.
 */
const ReasonDialog: React.FC<ReasonDialogProps> = ({
  open, onClose, title, facts, consequence, confirmText = 'Reverse', confirmColor = 'error',
  reasonPlaceholder = 'Why is this being reversed?', onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setReason(''); setError(null); }
  }, [open]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (err: any) {
      setError(err?.message || 'That didn\'t go through. Nothing was changed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConfirmationDialog
      open={open}
      onClose={busy ? () => undefined : onClose}
      onConfirm={confirm}
      title={title}
      message={consequence}
      confirmText={confirmText}
      confirmColor={confirmColor}
      loading={busy}
      confirmDisabled={!reason.trim()}
    >
      {facts && facts.length > 0 && <ConfirmFacts items={facts} />}
      <TextField
        label="Reason"
        placeholder={reasonPlaceholder}
        value={reason}
        onChange={e => setReason(e.target.value)}
        required
        fullWidth
        multiline
        minRows={2}
        size="small"
        disabled={busy}
        sx={{ mt: 2 }}
        inputProps={{ maxLength: 500 }}
        helperText="Saved with the record for audit."
      />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </ConfirmationDialog>
  );
};

export default ReasonDialog;
